import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LoaderCircle } from "lucide-react";
import type { AgentControlState, AgentProposal, WorkItem, WorkStatus } from "@business-ops/domain";
import { api, ApiError, AUTH_REQUIRED_EVENT, type Session, type WhatsAppConnection, type WhatsAppConversationRule } from "./api.js";
import { AppShell, type AppView, type OpsWorkspaceMode } from "./components/shell.js";
import { ProductOnboarding } from "./components/product-onboarding.js";
import { productApi, signOutWorkspace } from "./product-api.js";
import { AddWorkDrawer, WorkDetailDrawer } from "./components/work-forms.js";
import { ToastRegion, type ToastMessage } from "./components/ui.js";
import { opsEvidenceThreadReturnFromHistory, opsEvidenceVerificationFromUrl, type OpsEvidenceThreadReturnReceipt } from "./components/ops-assistant.js";
import { createTrailingRefresh, useBoard } from "./use-board.js";
import { approvalUnits, type AgentWorkQueue } from "./views/review.js";
import { OpsAgentView } from "./views/ops-agent.js";
import { canonicalActiveAgentProposals } from "./views/work.js";
import { ConversationsView } from "./views/conversations.js";
import { ActivityView, ClientsView, SettingsView } from "./views/secondary.js";
import { WhatsAppConnectionView } from "./views/whatsapp.js";
import { browserPrivateModeStorage, readPrivateMode, usePrivateModePresentation, writePrivateMode } from "./privacy-mode.js";

export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [requestedBusinessSlug, setRequestedBusinessSlug] = useState("");
  useEffect(() => {
    void api.session().then(setSession).catch((error: unknown) => {
      if (error instanceof ApiError && error.status === 401) setSession(null);
      else setSession(null);
    });
  }, []);
  if (session === undefined) return <BootScreen />;
  if (session === null) return <ProductOnboarding
    initialBusinessSlug={requestedBusinessSlug}
    legacyLogin={(login, password) => api.login(login, password)}
    loadWorkspaceSession={() => api.session()}
    onLogin={(nextSession) => { setRequestedBusinessSlug(""); setSession(nextSession); }}
  />;
  return <AuthenticatedApp
    key={session.workspace.id + ":" + session.member.id}
    session={session}
    onSignedOut={() => { setRequestedBusinessSlug(""); setSession(null); }}
    onSwitchBusiness={(businessSlug) => { setRequestedBusinessSlug(businessSlug); setSession(null); }}
  />;
}

function AuthenticatedApp({ session, onSignedOut, onSwitchBusiness }: {
  session: Session;
  onSignedOut: () => void;
  onSwitchBusiness: (businessSlug: string) => void;
}) {
  const privacyScope = { workspaceId: session.workspace.id, memberId: session.member.id };
  const [privateMode, setPrivateMode] = useState(() => readPrivateMode(browserPrivateModeStorage(), privacyScope));
  const [voicePresentationPrivacyLocked, setVoicePresentationPrivacyLocked] = useState(false);
  const voicePresentationPrivacyLockedRef = useRef(false);
  const [view, setViewState] = useState<AppView>(() => viewFromHash());
  const [opsMode, setOpsModeState] = useState<OpsWorkspaceMode>(() => opsWorkspaceModeFromHash(window.location.hash));
  const [mobileChatOpen, setMobileChatOpen] = useState(() => appMobileChatFromHistoryState(window.history.state));
  const [whatsAppSourceEventId, setWhatsAppSourceEventId] = useState<string | null>(
    () => whatsAppSourceFromUrl(window.location.href)
  );
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const [selected, setSelected] = useState<WorkItem | null>(null);
  const [adding, setAdding] = useState(false);
  const [addWorkDueDay, setAddWorkDueDay] = useState<string | null>(null);
  const [agentProposals, setAgentProposals] = useState<AgentProposal[]>([]);
  const [agentProposalHistory, setAgentProposalHistory] = useState<AgentProposal[]>([]);
  const [agentQueueLoadState, setAgentQueueLoadState] = useState<"LOADING" | "READY" | "STALE">("LOADING");
  const [agentControl, setAgentControl] = useState<AgentControlState | null>(null);
  const [agentConversations, setAgentConversations] = useState<WhatsAppConversationRule[]>([]);
  const [whatsAppConnectionStatus, setWhatsAppConnectionStatus] = useState<WhatsAppConnection["status"] | null>(null);
  usePrivateModePresentation(privateMode);
  const handleVoicePresentationPrivacyLockChange = useCallback((locked: boolean) => {
    voicePresentationPrivacyLockedRef.current = locked;
    setVoicePresentationPrivacyLocked(locked);
  }, []);
  const requestPrivateMode = useCallback((enabled: boolean) => {
    // The ref closes the event-to-render gap: both visible controls are also
    // disabled, but this owner rejects a same-tick click before React repaints.
    if (voicePresentationPrivacyLockedRef.current) return;
    setPrivateMode(enabled);
  }, []);
  useEffect(() => {
    writePrivateMode(browserPrivateModeStorage(), privacyScope, privateMode);
  }, [privacyScope.memberId, privacyScope.workspaceId, privateMode]);
  useEffect(() => {
    const requireAuth = () => onSignedOut();
    window.addEventListener(AUTH_REQUIRED_EVENT, requireAuth);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, requireAuth);
  }, [onSignedOut]);
  useEffect(() => {
    // WHY [BOL-OPS-HISTORY-RESTORE-001]: phone tools and Chat are two visual
    // states of one mounted workspace. Browser Back must restore the exact
    // prior component instance (filters, focus, scroll and draft), not infer a
    // fresh screen from whichever hash happens to remain. Falsifier: Back from
    // Ask Agent exits the app, remounts a tool, or leaves shell and URL apart.
    const restoreRoute = (event: PopStateEvent) => {
      const route = appRouteFromUrl(window.location.href);
      setViewState(route.view);
      setOpsModeState(route.opsMode);
      setWhatsAppSourceEventId(route.whatsAppSourceEventId);
      setMobileChatOpen(appMobileChatFromHistoryState(event.state));
    };
    window.addEventListener("popstate", restoreRoute);
    return () => window.removeEventListener("popstate", restoreRoute);
  }, []);
  const notify = useCallback((text: string, tone: "success" | "error" = "success") => {
    const id = Date.now() + Math.floor(Math.random() * 1_000);
    setMessages((current) => [...current.slice(-2), { id, text, tone }]);
    window.setTimeout(() => setMessages((current) => current.filter((message) => message.id !== id)), 4_500);
  }, []);
  const canConfigureAutomation = session.member.role === "OWNER" || session.member.role === "MANAGER";
  useEffect(() => {
    if (!canConfigureAutomation) {
      setWhatsAppConnectionStatus(null);
      return;
    }
    let active = true;
    const refreshConnection = () => {
      void api.whatsAppConnection()
        .then((connection) => { if (active) setWhatsAppConnectionStatus(connection.status); })
        .catch(() => { if (active) setWhatsAppConnectionStatus("UNAVAILABLE"); });
    };
    refreshConnection();
    // WHY [BOL-WHATSAPP-LIVE-SEND-GATE-001]: Manual approval must never look
    // sendable while the isolated Baileys socket is waiting for a QR scan. The
    // final server/connector guard remains authoritative; this low-rate status
    // refresh only prevents the exact frozen-click experience reported by the
    // operator. Do not infer readiness from saved conversations or transport
    // policy. Falsifier: an immediate Send stays enabled after SCAN_REQUIRED,
    // or remains locked more than one refresh after CONNECTED.
    const timer = window.setInterval(refreshConnection, 15_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [canConfigureAutomation]);
  const runAgentQueueRefresh = useCallback(async () => {
    if (!canConfigureAutomation) {
      setAgentProposals([]);
      setAgentProposalHistory([]);
      setAgentQueueLoadState("READY");
      setAgentControl(null);
      setAgentConversations([]);
      return;
    }
    setAgentQueueLoadState((current) => current === "STALE" ? "LOADING" : current);
    // WHY [BOL-ACTION-PROJECTION-LOAD-001]: an empty approval queue is a
    // business fact, while a pending/failed request is not. Loading Review,
    // controls, and the channel directory as one Promise.all previously made
    // any slow or failed companion request briefly erase real prepared work.
    // Keep the last proven actions, expose loading/stale truth to Work, and let
    // each optional projection recover independently. This boundary remains
    // channel-neutral when Instagram/Threads adapters join WhatsApp.
    // Falsifier: the owner sees "Nothing needs your approval" while /api/review
    // is pending/failing or because an unrelated adapter/control request fails.
    const reviewLoad = api.review().then((review) => {
      setAgentProposals(review.proposals);
      setAgentProposalHistory(review.history);
      setAgentQueueLoadState("READY");
    }).catch(() => {
      setAgentQueueLoadState("STALE");
    });
    const controlLoad = api.agentControl().then(setAgentControl).catch(() => undefined);
    const directoryLoad = api.whatsAppConversations()
      .then((whatsApp) => setAgentConversations(whatsApp.conversations))
      .catch(() => undefined);
    await Promise.all([reviewLoad, controlLoad, directoryLoad]);
  }, [canConfigureAutomation]);
  // WHY [BOL-ACTION-PROJECTION-TRAILING-001]: editing a scheduled message
  // commits a new Manual-review revision while SSE and the explicit refresh can
  // overlap. Unordered Review reads let an older response overwrite the newer
  // one, hiding reapproval behind an obsolete Sent card. Serialize bursts and
  // always finish with one trailing canonical read. Falsifier: Save for review
  // requires a page reload before the revised schedule appears.
  const refreshAgentQueue = useMemo(
    () => createTrailingRefresh(runAgentQueueRefresh),
    [runAgentQueueRefresh]
  );
  const boardController = useBoard(useCallback((message: string) => notify(message, "error"), [notify]), refreshAgentQueue);
  const canonicalAgentProposals = useMemo(
    () => boardController.board
      ? canonicalActiveAgentProposals(boardController.board, agentProposals)
      : [],
    [agentProposals, boardController.board]
  );
  useEffect(() => { void refreshAgentQueue(); }, [refreshAgentQueue]);
  const canArchiveWork = session.member.role === "OWNER" || session.member.role === "MANAGER";
  const activeView = ["conversations", "whatsapp"].includes(view) && !canConfigureAutomation ? "ops" : view;

  const setView = (next: AppView) => {
    setViewState(next);
    setMobileChatOpen(false);
    setWhatsAppSourceEventId(null);
    pushAppRoute(next === "ops" ? opsModeHash(opsMode) : `#${next}`);
  };
  const openConversations = (sourceEventId?: string, returnReceipt?: OpsEvidenceThreadReturnReceipt | null) => {
    const exactSource = normalizeSourceEventId(sourceEventId ?? null);
    // WHY [BOL-OPS-SOURCE-COVERAGE-RETURN-001]: lazy coverage links enter the
    // conversation directory without a proof URL, so the existing proof-only
    // return marker was absent and the human had no visible Back to chat.
    // Preserve only the exact originating turn/scroll receipt supplied by the
    // mounted Chat thread; direct Conversations entries remain ordinary.
    // Falsifier: coverage opens the wrong turn/latest Chat, invents a claim or
    // proof route, or direct/conversation navigation gains a false origin.
    const sourceReturn = returnReceipt ?? null;
    const evidenceReturn = sourceReturn ?? opsEvidenceThreadReturnFromHistory(window.history.state);
    const openedFromOpsProof = Boolean(
      opsEvidenceVerificationFromUrl(window.location.href)
      && evidenceReturn
      && appRouteFromUrl(window.location.href).view !== "conversations"
    );
    const continuesOpsProofDrilldown = Boolean(
      sourceReturn
      || (evidenceReturn
        && (openedFromOpsProof || appOpsChatReturnAvailable(window.location.href, window.history.state))
      )
    );
    setViewState("conversations");
    setMobileChatOpen(false);
    setWhatsAppSourceEventId(exactSource);
    pushAppRoute(
      "#conversations",
      exactSource,
      {
        historyState: sourceReturn
          ? appHistoryStateForSourceConversation(window.history.state, sourceReturn)
          : continuesOpsProofDrilldown
            ? appHistoryStateForConversation(window.history.state, true)
            : window.history.state,
        preserveOpsEvidenceDrilldown: !sourceReturn && continuesOpsProofDrilldown,
        preserveOpsChatReturn: Boolean(sourceReturn)
      }
    );
  };
  const setOpsMode = (next: OpsWorkspaceMode) => {
    // WHY [BOL-OPS-SURFACE-ROUTE-001]: Board, Calendar, and Approvals are
    // interchangeable views over one live Ops workspace. Preserve the mounted
    // conversation while making each surface directly linkable. Falsifier: a
    // tool click remounts chat, clears its draft, or opens a second task state.
    setViewState("ops");
    setOpsModeState(next);
    setMobileChatOpen(false);
    setWhatsAppSourceEventId(null);
    pushAppRoute(opsModeHash(next));
  };
  const openMobileChat = () => {
    if (mobileChatOpen) return;
    setMobileChatOpen(true);
    pushMobileChatRoute();
  };
  const openAddWork = (dueDay: string | null = null) => {
    setAddWorkDueDay(dueDay);
    setAdding(true);
  };
  const closeAddWork = () => {
    setAdding(false);
    setAddWorkDueDay(null);
  };
  const moveWork = async (item: WorkItem, status: WorkStatus) => {
    try { await boardController.updateWork(item, { status }); notify(`Moved to ${status.toLowerCase().replaceAll("_", " ")}.`); }
    catch (error) { notify(error instanceof Error ? error.message : "That move did not save.", "error"); }
  };
  const logout = async () => {
    try {
      const kind = await signOutWorkspace({ apiClient: productApi, legacyLogout: () => api.logout() });
      onSignedOut();
      if (kind === "PRODUCT") window.location.replace("/");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Sign-out could not finish safely. Please try again.", "error");
    }
  };
  const agentQueue: AgentWorkQueue = {
    proposals: canonicalAgentProposals,
    history: agentProposalHistory,
    loadState: agentQueueLoadState,
    refresh: refreshAgentQueue,
    conversations: agentConversations,
    transportMode: agentControl?.transportMode ?? "BLOCKED",
    whatsAppConnectionStatus,
    openWhatsApp: () => setView("whatsapp"),
    notify,
    removeProposal: (id) => setAgentProposals((current) => current.filter((proposal) => proposal.id !== id)),
    replaceProposal: (updated) => setAgentProposals((current) => current.map((proposal) => proposal.id === updated.id ? updated : proposal)),
    replaceConversation: (updated) => setAgentConversations((current) => current.map((conversation) => conversation.conversationId === updated.conversationId ? updated : conversation)),
    afterBoardChange: async () => {
      await Promise.all([boardController.refresh(), refreshAgentQueue()]);
    }
  };

  if (boardController.loading && !boardController.board) return <BootScreen label="Loading board." />;
  if (!boardController.board) return <FailureScreen retry={() => void boardController.refresh()} />;
  const board = boardController.board;

  return (
    <>
      <AppShell session={session} view={mobileChatOpen ? "ops" : activeView} setView={setView} opsMode={mobileChatOpen ? "today" : opsMode} setOpsMode={setOpsMode} logout={() => void logout()} connection={boardController.loading ? "refreshing" : "live"} actionCount={approvalCountForQueue(canonicalAgentProposals)} privateMode={privateMode} privateModeLocked={voicePresentationPrivacyLocked} setPrivateMode={requestPrivateMode}>
        {activeView === "ops" ? <OpsAgentView
          board={board}
          queue={agentQueue}
          historyScope={`${session.workspace.id}:${session.member.id}`}
          publicReleaseId={session.publicReleaseId}
          privateMode={privateMode}
          onVoicePresentationPrivacyLockChange={handleVoicePresentationPrivacyLockChange}
          displayName={session.member.displayName}
          mode={opsMode}
          setMode={setOpsMode}
          openWork={setSelected}
          moveWork={moveWork}
          addWork={() => openAddWork()}
          addWorkForDay={(dayKey) => openAddWork(dayKey)}
          openConversations={canConfigureAutomation ? openConversations : undefined}
          mobileChatOpen={mobileChatOpen}
          openMobileChat={openMobileChat}
        /> : null}
        {activeView === "clients" ? <ClientsView board={board} openWork={setSelected} /> : null}
        {activeView === "activity" ? <ActivityView afterUndo={boardController.refresh} notify={notify} /> : null}
        {activeView === "conversations" ? <ConversationsView
          focusSourceEventId={whatsAppSourceEventId}
          focusSource={openConversations}
          clearFocusSource={() => openConversations()}
          {...(appOpsChatReturnAvailable(window.location.href, window.history.state)
            ? { returnToOpsChat: () => {
                if (!returnToOpsChatFromHistory()) return;
                setViewState("ops");
                setOpsModeState("today");
                setMobileChatOpen(false);
                setWhatsAppSourceEventId(null);
              } }
            : {})}
          openWork={(workItemId) => {
            const workItem = board.workItems.find((candidate) => candidate.id === workItemId);
            if (workItem) {
              setSelected(workItem);
              return;
            }
            setOpsMode("board");
            void boardController.refresh();
            notify("Task is refreshing. Open it from the board.");
          }}
          openApprovals={() => setOpsMode("approvals")}
        /> : null}
        {activeView === "whatsapp" ? <WhatsAppConnectionView notify={notify} /> : null}
        {activeView === "settings" ? <SettingsView board={board} canConfigure={canConfigureAutomation} notify={notify} openWhatsApp={canConfigureAutomation ? () => setView("whatsapp") : undefined} afterProposalRestore={refreshAgentQueue} privateMode={privateMode} setPrivateMode={requestPrivateMode} onSwitchBusiness={onSwitchBusiness} /> : null}
      </AppShell>
      <AddWorkDrawer open={adding} initialDueDay={addWorkDueDay} board={board} close={closeAddWork} create={boardController.createWork} notify={notify} />
      <WorkDetailDrawer selected={selected} board={board} canArchive={canArchiveWork} close={() => setSelected(null)} update={boardController.updateWork} notify={notify} agentQueue={agentQueue} />
      <ToastRegion messages={messages} dismiss={(id) => setMessages((current) => current.filter((message) => message.id !== id))} />
    </>
  );
}

export function approvalCountForQueue(proposals: readonly AgentProposal[]): number {
  // WHY [BOL-APPROVAL-COUNT-001]: one manager decision can contain a Task and
  // its linked Message while both effects stay separately audited. Count the
  // same complete review units the operator browses, never raw proposal rows.
  // Falsifier: one Task + Message bundle shows as two actions in the shell.
  return approvalUnits(proposals).length;
}

function BootScreen({ label = "Opening your production desk." }: { label?: string }) {
  return <main className="boot-screen"><span className="brand-mark brand-mark--large" aria-hidden="true"><span /></span><LoaderCircle className="spin" size={20} /><p>{label}</p></main>;
}

function FailureScreen({ retry }: { retry: () => void }) {
  return <main className="boot-screen"><h1>The board could not be loaded.</h1><p>Check the connection and try again.</p><button className="primary-button" type="button" onClick={retry}>Try again</button></main>;
}

function viewFromHash(): AppView {
  return appViewFromHash(window.location.hash);
}

const SOURCE_EVENT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

function normalizeSourceEventId(value: string | null): string | null {
  return value && SOURCE_EVENT_ID_PATTERN.test(value) ? value.toLocaleLowerCase() : null;
}

export function whatsAppSourceFromUrl(value: string): string | null {
  try {
    return normalizeSourceEventId(new URL(value, "https://business-ops.invalid").searchParams.get("source"));
  } catch {
    return null;
  }
}

const MOBILE_CHAT_HISTORY_KEY = "businessOpsMobileChat";
const OPS_CHAT_RETURN_HISTORY_KEY = "businessOpsChatReturn";
const OPS_CHAT_RETURN_HISTORY_SCHEMA = "ops.chat-return.v1";
const OPS_EVIDENCE_HISTORY_KEYS = [
  "businessOpsEvidenceAnchor",
  "businessOpsEvidenceProof",
  "businessOpsEvidenceReturn",
  OPS_CHAT_RETURN_HISTORY_KEY
] as const;

export function appMobileChatFromHistoryState(value: unknown): boolean {
  return Boolean(value && typeof value === "object" && !Array.isArray(value)
    && (value as Record<string, unknown>)[MOBILE_CHAT_HISTORY_KEY] === true);
}

export function appHistoryStateWithMobileChat(value: unknown, open: boolean): Record<string, unknown> {
  const prior = value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  return { ...prior, [MOBILE_CHAT_HISTORY_KEY]: open };
}

export function appHasOpsChatReturnMarker(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = (value as Record<string, unknown>)[OPS_CHAT_RETURN_HISTORY_KEY];
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) return false;
  const record = candidate as Record<string, unknown>;
  return record.schema === OPS_CHAT_RETURN_HISTORY_SCHEMA;
}

export function appHistoryStateForConversation(value: unknown, retainOpsChatReturn: boolean): Record<string, unknown> {
  const next = appHistoryStateWithMobileChat(value, false);
  if (retainOpsChatReturn) {
    next[OPS_CHAT_RETURN_HISTORY_KEY] = { schema: OPS_CHAT_RETURN_HISTORY_SCHEMA };
  } else {
    delete next[OPS_CHAT_RETURN_HISTORY_KEY];
  }
  return next;
}

export function appHistoryStateForSourceConversation(
  value: unknown,
  returnReceipt: OpsEvidenceThreadReturnReceipt
): Record<string, unknown> {
  return appHistoryStateForConversation({
    ...appHistoryStateWithoutOpsEvidenceDrilldown(value),
    businessOpsEvidenceReturn: returnReceipt
  }, true);
}

export function appHistoryStateWithoutOpsEvidenceDrilldown(value: unknown): Record<string, unknown> {
  const next = appHistoryStateWithMobileChat(value, false);
  for (const key of OPS_EVIDENCE_HISTORY_KEYS) delete next[key];
  return next;
}

interface OpsChatHistoryTarget {
  location: Pick<Location, "href">;
  history: Pick<History, "state" | "pushState">;
}

export function appOpsChatReturnAvailable(value: string, state: unknown): boolean {
  const receipt = opsEvidenceThreadReturnFromHistory(state);
  if (!receipt) return false;
  if (appHasOpsChatReturnMarker(state)) return true;
  const proof = opsEvidenceVerificationFromUrl(value);
  const sourceEventId = whatsAppSourceFromUrl(value);
  // Upgrade recovery for a source page opened before the return marker existed:
  // the content-free proof route, exact source, and receipt must all agree.
  return appRouteFromUrl(value).view === "conversations"
    && proof !== null
    && sourceEventId === proof.sourceEventId
    && receipt.turnId === proof.turnId;
}

function appHistoryStateForOpsChatReturn(value: unknown): Record<string, unknown> {
  const next = appHistoryStateWithMobileChat(value, false);
  delete next.businessOpsEvidenceAnchor;
  delete next.businessOpsEvidenceProof;
  delete next[OPS_CHAT_RETURN_HISTORY_KEY];
  return next;
}

export function returnToOpsChatFromHistory(
  target: OpsChatHistoryTarget | null = typeof window === "undefined" ? null : window
): boolean {
  if (!target || !appOpsChatReturnAvailable(target.location.href, target.history.state)) return false;
  // WHY [BOL-OPS-SOURCE-RETURN-001]: the source entry already owns the exact
  // content-free Chat turn/scroll receipt. Route directly to canonical Chat
  // with that receipt instead of guessing how many proof/list history entries
  // precede it. Keep the receipt reusable for Back/Forward; unrelated pushes
  // strip it below. Falsifier: Back to chat opens proof/the directory, jumps to
  // latest after Forward/reload, or appears on direct Conversations entry.
  target.history.pushState(
    appHistoryStateForOpsChatReturn(target.history.state),
    "",
    appPathForRoute(target.location.href, "#ops", null, false)
  );
  return true;
}

export interface AppRouteState {
  view: AppView;
  opsMode: OpsWorkspaceMode;
  whatsAppSourceEventId: string | null;
}

export function appRouteFromUrl(value: string): AppRouteState {
  const url = new URL(value, "https://business-ops.invalid");
  const view = appViewFromHash(url.hash);
  return {
    view,
    opsMode: opsWorkspaceModeFromHash(url.hash),
    whatsAppSourceEventId: view === "conversations" ? normalizeSourceEventId(url.searchParams.get("source")) : null
  };
}

export function appPathForRoute(
  value: string,
  hash: string,
  sourceEventId: string | null,
  preserveOpsEvidenceDrilldown: boolean
): string {
  const url = new URL(value, "https://business-ops.invalid");
  if (!preserveOpsEvidenceDrilldown) {
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith("opsProof")) url.searchParams.delete(key);
    }
  }
  if (sourceEventId) url.searchParams.set("source", sourceEventId);
  else url.searchParams.delete("source");
  url.hash = hash;
  return `${url.pathname}${url.search}${url.hash}`;
}

function pushAppRoute(hash: string, sourceEventId: string | null = null, options: {
  historyState?: unknown;
  preserveOpsEvidenceDrilldown?: boolean;
  preserveOpsChatReturn?: boolean;
} = {}): void {
  const preserveOpsEvidenceDrilldown = options.preserveOpsEvidenceDrilldown === true;
  const next = appPathForRoute(
    window.location.href,
    hash,
    sourceEventId,
    preserveOpsEvidenceDrilldown
  );
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  // Proof-return receipts stay on the original summary and its matching
  // drilldown only. Sanitizing the newly pushed unrelated entry does not alter
  // that earlier R50 owner, so browser Back can still restore its turn/scroll.
  // A coverage source has a Chat origin but no semantic proof route. Keep
  // that return receipt while stripping any stale proof URL parameters.
  const nextState = preserveOpsEvidenceDrilldown || options.preserveOpsChatReturn === true
    ? appHistoryStateWithMobileChat(options.historyState ?? window.history.state, false)
    : appHistoryStateWithoutOpsEvidenceDrilldown(options.historyState ?? window.history.state);
  if (next === current && !appMobileChatFromHistoryState(window.history.state)) return;
  window.history.pushState(nextState, "", next);
}

function pushMobileChatRoute(): void {
  window.history.pushState(
    appHistoryStateWithMobileChat(window.history.state, true),
    "",
    `${window.location.pathname}${window.location.search}${window.location.hash}`
  );
}

export function appViewFromHash(hash: string): AppView {
  const candidate = hash.replace(/^#/, "");
  // WHY [BOL-ONE-WORKSPACE-001]: Board and Calendar are tools inside the
  // persistent Ops Agent workspace. Legacy bookmarks must enter that shell,
  // not remount the chat as a separate page and lose the operator's context.
  // Falsifier: #work or #calendar renders a standalone daily destination.
  if (["", "overview", "today", "review", "approvals", "work", "board", "calendar"].includes(candidate)) return "ops";
  return ["ops", "clients", "activity", "conversations", "whatsapp", "settings"].includes(candidate) ? candidate as AppView : "ops";
}

export function opsWorkspaceModeFromHash(hash: string): OpsWorkspaceMode {
  const candidate = hash.replace(/^#/, "");
  if (candidate === "calendar") return "calendar";
  if (candidate === "work" || candidate === "board") return "board";
  if (candidate === "review" || candidate === "approvals") return "approvals";
  return "today";
}

function opsModeHash(mode: OpsWorkspaceMode): string {
  return mode === "today" ? "#ops" : `#${mode}`;
}
